// Access-link gate for the host portal.
//
// The portal is distributed as a per-host link (…/?id=<hostId>), but nothing
// stopped the bare URL from booting the dashboard. Without an id every screen
// fired ?hostId=null at the API (500s) and rendered "Unique ID :" blanks with
// zeroed earnings — it looked like a broken product rather than a missing link.
//
// Mirrors the BD portal's BdIdGuard so all three partner surfaces behave the same.
import { useEffect } from "react";

const ID_PARAM = "id";
const STORAGE_KEY = "hostId";

// Resolves the host id from the URL, falling back to the one persisted on a
// previous visit, and keeps localStorage in sync with an id that came from the
// link (so refreshing a deep route still works).
export function useAccessId() {
  let id = null;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(ID_PARAM)?.trim();
    const fromStorage = localStorage.getItem(STORAGE_KEY)?.trim();
    id = fromUrl || fromStorage || null;
    if (fromUrl) localStorage.setItem(STORAGE_KEY, fromUrl);
  } catch {
    id = null;
  }
  return id;
}

export default function AccessGuard({ children }) {
  const id = useAccessId();

  // The old entry wrote localStorage.setItem("hostId", null) unconditionally,
  // leaving the literal string "null" in storage forever after one bare visit.
  useEffect(() => {
    if (!id) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  }, [id]);

  if (id) return children;

  return (
    <div className="mx-guard" style={styles.screen}>
      <div className="mx-guard__card" style={styles.card}>
        <div style={styles.iconWrap}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--mx-pink)" strokeWidth="1.8" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 style={styles.title}>Access Denied</h2>
        <p style={styles.desc}>
          No host ID was provided. Please open the portal with the link your
          administrator sent you.
        </p>
        <div style={styles.codeBlock}>
          <code style={styles.code}>
            https://host.mxchichat.mimeld.com/?id=YOUR_HOST_ID
          </code>
        </div>
        <p style={styles.hint}>
          Contact your administrator if you believe this is a mistake.
        </p>
      </div>
    </div>
  );
}

const styles = {
  screen: {
    minHeight: "100vh",
    backgroundColor: "var(--mx-bg-2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: '"Poppins", system-ui, sans-serif',
    padding: 20,
  },
  card: {
    backgroundColor: "var(--mx-card)",
    border: "1px solid var(--mx-border)",
    borderRadius: "16px",
    padding: "48px 40px",
    maxWidth: "420px",
    width: "100%",
    textAlign: "center",
  },
  iconWrap: { marginBottom: "20px" },
  title: { color: "var(--mx-text)", fontSize: "22px", fontWeight: "600", margin: "0 0 12px" },
  desc: { color: "var(--mx-text-2)", fontSize: "14px", lineHeight: "1.6", margin: "0 0 20px" },
  codeBlock: {
    backgroundColor: "var(--mx-bg-2)",
    border: "1px solid var(--mx-border)",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "20px",
    wordBreak: "break-all",
  },
  code: { color: "var(--mx-accent-light)", fontSize: "12px" },
  hint: { color: "var(--mx-muted)", fontSize: "13px", margin: 0 },
};
